// Shared navigation registry — the single source of truth for sidebar nav
// groups. Both Layout's sidebar and the command palette consume
// buildNavigationGroups() so gating logic lives in one place.
//
// Nav arrays are module-level constants (referentially stable) so Layout can
// reference them in a useMemo dependency array without extra useMemo wrappers.

import {
  LayoutDashboard,
  FileSignature,
  ScrollText,
  CalendarClock,
  Handshake,
  Settings,
  Scale,
  Building2,
  LayoutTemplate,
  Users,
  BarChart3,
} from "lucide-react";

export interface NavItem {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
}

export interface NavGroup {
  label?: string;
  items: NavItem[];
}

export const topNavigation: NavItem[] = [
  { name: "Dashboard", href: "/", icon: LayoutDashboard },
];

export const generateNavigation: NavItem[] = [
  { name: "Document Drafts", href: "/generate/contracts", icon: FileSignature },
];

export const manageNavigation: NavItem[] = [
  { name: "Contract Register", href: "/manage/contracts", icon: ScrollText },
  { name: "Schedule", href: "/manage/schedule", icon: CalendarClock },
  { name: "Suppliers", href: "/manage/suppliers", icon: Handshake },
];

export const adminNavigation: NavItem[] = [
  { name: "Organisation", href: "/organisation", icon: Building2 },
  { name: "Templates", href: "/generate/templates", icon: LayoutTemplate },
  { name: "Clause Admin", href: "/generate/clauses", icon: Scale },
];

export const globalAdminNavigation: NavItem[] = [
  { name: "Organisations", href: "/admin/organisations", icon: Building2 },
  { name: "Global Admins", href: "/admin/users", icon: Users },
  { name: "Statistics", href: "/admin/statistics", icon: BarChart3 },
];

export const settingsNavigation: NavItem[] = [
  { name: "Settings", href: "/settings", icon: Settings },
];

/**
 * Assembles the ordered nav groups for the given user context. Pure function —
 * no React, safe to call outside components.
 *
 * Group order mirrors the current Layout sidebar:
 *   [Dashboard] → Generation? → Manage? → (Global Admin | Admin)? → Settings
 *
 * Role precedence: `global-admin` replaces `admin` — a global-admin user never
 * sees the org-level Admin group.
 */
export function buildNavigationGroups(
  role: string | undefined,
  hasArea: (area: string) => boolean,
): NavGroup[] {
  const groups: NavGroup[] = [{ items: topNavigation }];

  if (hasArea("generate")) {
    groups.push({ label: "Generation", items: generateNavigation });
  }

  if (hasArea("manage")) {
    groups.push({ label: "Manage", items: manageNavigation });
  }

  if (role === "global-admin") {
    groups.push({ label: "Global Admin", items: globalAdminNavigation });
  } else if (role === "admin") {
    groups.push({ label: "Admin", items: adminNavigation });
  }

  groups.push({ items: settingsNavigation });

  return groups;
}
